import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
    Button,
    Input,
    Select,
    Switch,
    Space,
    Card,
    Row,
    Col,
    Tag,
    message,
    DatePicker,
    Divider,
    Tooltip,
    Popconfirm,
    Typography,
    Alert,
    Spin,
} from 'antd';
import {
    PlusOutlined,
    CloseOutlined,
    SaveOutlined,
    EditOutlined,
    FileTextOutlined,
    TagsOutlined,
    FolderOutlined,
    UserOutlined,
    CalendarOutlined,
    StarOutlined,
    GlobalOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { createBlogCategory, createBlogTag } from '../../../redux/slice/blog.slice';
import moment from 'moment';
import 'react-quill/dist/quill.snow.css';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

// Dynamic import for ReactQuill
const ReactQuill = React.lazy(() => import('react-quill'));

// Quill editor modules and formats
const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
};

const quillFormats = ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link'];

// Simple Quill editor for excerpt (limited toolbar)
const excerptQuillModules = {
    toolbar: [['bold', 'italic'], ['link'], ['clean']],
};

const excerptQuillFormats = ['bold', 'italic', 'link'];

// Quill Editor Component with fallback
const QuillEditor = ({
    value,
    onChange,
    modules,
    formats,
    placeholder,
    height,
    isExcerpt = false,
}) => {
    const [quillLoaded, setQuillLoaded] = useState(false);
    const [quillError, setQuillError] = useState(false);

    useEffect(() => {
        // Check if ReactQuill is available
        const checkQuill = async () => {
            try {
                const ReactQuillModule = await import('react-quill');
                if (ReactQuillModule.default) {
                    setQuillLoaded(true);
                }
            } catch (error) {
                console.warn('ReactQuill not available, falling back to textarea');
                setQuillError(true);
            }
        };

        checkQuill();
    }, []);

    if (quillError) {
        return (
            <TextArea
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                rows={isExcerpt ? 4 : 12}
                className="w-full"
            />
        );
    }

    if (!quillLoaded) {
        return (
            <div
                className="flex items-center justify-center"
                style={{ height: height || '200px' }}
            >
                <Spin size="large" />
                <Text className="ml-2">Loading editor...</Text>
            </div>
        );
    }

    return (
        <React.Suspense
            fallback={
                <div
                    className="flex items-center justify-center"
                    style={{ height: height || '200px' }}
                >
                    <Spin size="large" />
                    <Text className="ml-2">Loading editor...</Text>
                </div>
            }
        >
            <ReactQuill
                theme="snow"
                value={value || ''}
                onChange={onChange}
                modules={modules}
                formats={formats}
                placeholder={placeholder}
                style={{ height: height || '200px', marginBottom: '50px' }}
            />
        </React.Suspense>
    );
};

export default function BlogForm({ onSubmit, categories, tags, initialData, onCancel }) {
    const dispatch = useDispatch();
    const [selectedTags, setSelectedTags] = useState([]);
    const [newCategory, setNewCategory] = useState('');
    const [newTag, setNewTag] = useState('');
    const [showNewCategory, setShowNewCategory] = useState(false);
    const [showNewTag, setShowNewTag] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isDirty },
        reset,
    } = useForm({
        defaultValues: {
            title: '',
            content: '',
            excerpt: '',
            author: '',
            status: 'draft',
            is_featured: false,
            category: '',
            tags: [],
            meta_title: '',
            meta_description: '',
            meta_keywords: '',
            published_date: null,
            seo_url: '',
        },
    });

    const watchedValues = watch();

    useEffect(() => {
        if (initialData) {
            reset({
                title: initialData.title || '',
                content: initialData.content || '',
                excerpt: initialData.excerpt || '',
                author: initialData.author || '',
                status: initialData.status || 'draft',
                is_featured: initialData.is_featured || false,
                category:
                    initialData.categories?.[0]?._id || initialData.categories?.[0] || '',
                tags: initialData.tags?.map((tag) => tag._id) || [],
                meta_title: initialData.seo_title || '',
                meta_description: initialData.seo_description || '',
                meta_keywords: initialData.seo_keywords?.join(', ') || '',
                published_date: initialData.published_at
                    ? moment(initialData.published_at)
                    : null,
                seo_url: initialData.slug || '',
            });
            setSelectedTags(initialData.tags || []);
        }
    }, [initialData, reset]);

    // Auto-generate SEO URL from title
    useEffect(() => {
        if (watchedValues.title && !initialData?.slug) {
            const seoUrl = watchedValues.title
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .trim();
            setValue('seo_url', seoUrl);
        }
    }, [watchedValues.title, setValue, initialData]);

    const handleFormSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const formData = {
                ...data,
                tags: selectedTags ? selectedTags.map((tag) => tag._id || tag) : [],
                published_at: data.published_date
                    ? data.published_date.toISOString()
                    : null,
                // Map SEO fields to backend expected names
                seo_title: data.meta_title,
                seo_description: data.meta_description,
                seo_keywords: data.meta_keywords
                    ? data.meta_keywords.split(',').map((k) => k.trim())
                    : [],
            };
            await onSubmit(formData);
        } catch (error) {
            console.error('Form submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreateCategory = async () => {
        if (!newCategory.trim()) {
            message.error('Category name is required');
            return;
        }

        try {
            const categoryData = {
                name: newCategory.trim(),
                description: '',
                color: '#007bff',
            };
            await dispatch(createBlogCategory(categoryData)).unwrap();
            setNewCategory('');
            setShowNewCategory(false);
            message.success('Category created successfully');
        } catch (error) {
            message.error(error.message || 'Failed to create category');
        }
    };

    const handleCreateTag = async () => {
        if (!newTag.trim()) {
            message.error('Tag name is required');
            return;
        }

        try {
            const tagData = {
                name: newTag.trim(),
                description: '',
                color: '#6c757d',
            };
            const result = await dispatch(createBlogTag(tagData)).unwrap();
            setSelectedTags([...selectedTags, result]);
            setNewTag('');
            setShowNewTag(false);
            message.success('Tag created successfully');
        } catch (error) {
            message.error(error.message || 'Failed to create tag');
        }
    };

    const handleTagSelect = (tagIds) => {
        if (!tags) return;

        // Convert tagIds to tag objects
        const selectedTagObjects = tagIds.map((tagId) => {
            const tag = tags.find((t) => t._id === tagId);
            return tag || { _id: tagId, name: 'Unknown Tag' };
        });

        setSelectedTags(selectedTagObjects);
    };

    const handleTagRemove = (tagId) => {
        setSelectedTags((selectedTags || []).filter((tag) => tag._id !== tagId));
    };

    return (
        <div className="max-h-[80vh] overflow-y-auto">
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <Title level={3} className="mb-1 flex items-center">
                                <FileTextOutlined className="mr-2 text-blue-600" />
                                {initialData ? 'Edit Blog Post' : 'Create New Blog Post'}
                            </Title>
                            <Text type="secondary">
                                {initialData
                                    ? 'Update your blog post content and settings'
                                    : 'Fill in the details to create a new blog post'}
                            </Text>
                        </div>
                        <div className="flex items-center space-x-2">
                            {isDirty && (
                                <Alert
                                    message="Unsaved changes"
                                    type="warning"
                                    showIcon
                                    className="mr-2"
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Basic Information */}
                <Card
                    title={
                        <div className="flex items-center">
                            <UserOutlined className="mr-2 text-blue-600" />
                            <span>Basic Information</span>
                        </div>
                    }
                    size="small"
                    className="shadow-sm border-l-4 border-l-blue-500"
                >
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Title *
                                </label>
                                <Controller
                                    name="title"
                                    control={control}
                                    rules={{ required: 'Title is required' }}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            placeholder="Enter a compelling blog title..."
                                            size="large"
                                            className={`${
                                                errors.title ? 'border-red-500' : ''
                                            } hover:border-blue-400 focus:border-blue-500`}
                                        />
                                    )}
                                />
                                {errors.title && (
                                    <Text type="danger" className="text-xs mt-1">
                                        {errors.title.message}
                                    </Text>
                                )}
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Author
                                </label>
                                <Controller
                                    name="author"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            placeholder="Enter author name"
                                            prefix={
                                                <UserOutlined className="text-gray-400" />
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Status
                                </label>
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field }) => (
                                        <Select {...field} className="w-full">
                                            <Option value="draft">
                                                <div className="flex items-center">
                                                    <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                                                    Draft
                                                </div>
                                            </Option>
                                            <Option value="published">
                                                <div className="flex items-center">
                                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                                    Published
                                                </div>
                                            </Option>
                                            <Option value="archived">
                                                <div className="flex items-center">
                                                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                                                    Archived
                                                </div>
                                            </Option>
                                        </Select>
                                    )}
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Published Date
                                </label>
                                <Controller
                                    name="published_date"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            className="w-full"
                                            showTime
                                            placeholder="Select date and time"
                                            prefix={
                                                <CalendarOutlined className="text-gray-400" />
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Featured Post
                                </label>
                                <div className="flex items-center">
                                    <Controller
                                        name="is_featured"
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                {...field}
                                                checked={field.value}
                                                checkedChildren={<StarOutlined />}
                                                unCheckedChildren={<StarOutlined />}
                                            />
                                        )}
                                    />
                                    <span className="ml-3 text-sm text-gray-600">
                                        Mark as featured post
                                    </span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Category and Tags */}
                <Card
                    title={
                        <div className="flex items-center">
                            <TagsOutlined className="mr-2 text-green-600" />
                            <span>Categories & Tags</span>
                        </div>
                    }
                    size="small"
                    className="shadow-sm border-l-4 border-l-green-500"
                >
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Category
                                </label>
                                <div className="flex gap-2">
                                    <Controller
                                        name="category"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                placeholder="Select category"
                                                className="flex-1"
                                                dropdownRender={(menu) => (
                                                    <div>
                                                        {menu}
                                                        <Divider
                                                            style={{ margin: '8px 0' }}
                                                        />
                                                        {showNewCategory ? (
                                                            <div className="flex gap-2 p-2">
                                                                <Input
                                                                    value={newCategory}
                                                                    onChange={(e) =>
                                                                        setNewCategory(
                                                                            e.target
                                                                                .value,
                                                                        )
                                                                    }
                                                                    placeholder="New category name"
                                                                    onPressEnter={
                                                                        handleCreateCategory
                                                                    }
                                                                />
                                                                <Button
                                                                    type="primary"
                                                                    size="small"
                                                                    onClick={
                                                                        handleCreateCategory
                                                                    }
                                                                >
                                                                    Add
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setShowNewCategory(
                                                                            false,
                                                                        );
                                                                        setNewCategory(
                                                                            '',
                                                                        );
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <Button
                                                                type="text"
                                                                icon={<PlusOutlined />}
                                                                onClick={() =>
                                                                    setShowNewCategory(
                                                                        true,
                                                                    )
                                                                }
                                                                className="w-full"
                                                            >
                                                                Add New Category
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            >
                                                {categories &&
                                                    categories.map((category) => (
                                                        <Option
                                                            key={category._id}
                                                            value={category._id}
                                                        >
                                                            <div className="flex items-center">
                                                                <div
                                                                    className="w-3 h-3 rounded-full mr-2"
                                                                    style={{
                                                                        backgroundColor:
                                                                            category.color,
                                                                    }}
                                                                />
                                                                {category.name}
                                                            </div>
                                                        </Option>
                                                    ))}
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Tags
                                </label>
                                <Select
                                    mode="multiple"
                                    placeholder="Select tags"
                                    value={
                                        selectedTags
                                            ? selectedTags.map((tag) => tag._id)
                                            : []
                                    }
                                    onChange={handleTagSelect}
                                    className="w-full"
                                    dropdownRender={(menu) => (
                                        <div>
                                            {menu}
                                            <Divider style={{ margin: '8px 0' }} />
                                            {showNewTag ? (
                                                <div className="flex gap-2 p-2">
                                                    <Input
                                                        value={newTag}
                                                        onChange={(e) =>
                                                            setNewTag(e.target.value)
                                                        }
                                                        placeholder="New tag name"
                                                        onPressEnter={handleCreateTag}
                                                    />
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        onClick={handleCreateTag}
                                                    >
                                                        Add
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            setShowNewTag(false);
                                                            setNewTag('');
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button
                                                    type="text"
                                                    icon={<PlusOutlined />}
                                                    onClick={() => setShowNewTag(true)}
                                                    className="w-full"
                                                >
                                                    Add New Tag
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                >
                                    {tags &&
                                        tags.map((tag) => (
                                            <Option key={tag._id} value={tag._id}>
                                                <div className="flex items-center">
                                                    <div
                                                        className="w-3 h-3 rounded-full mr-2"
                                                        style={{
                                                            backgroundColor: tag.color,
                                                        }}
                                                    />
                                                    {tag.name}
                                                </div>
                                            </Option>
                                        ))}
                                </Select>
                                <div className="mt-2">
                                    {selectedTags &&
                                        selectedTags.map((tag) => (
                                            <Tag
                                                key={tag._id}
                                                closable
                                                onClose={() => handleTagRemove(tag._id)}
                                                color={tag.color}
                                                className="mb-1"
                                            >
                                                {tag.name}
                                            </Tag>
                                        ))}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Content */}
                <Card
                    title={
                        <div className="flex items-center">
                            <FileTextOutlined className="mr-2 text-purple-600" />
                            <span>Content</span>
                        </div>
                    }
                    size="small"
                    className="shadow-sm border-l-4 border-l-purple-500"
                >
                    <div className="mb-6">
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                            Excerpt/Description
                        </label>
                        <Controller
                            name="excerpt"
                            control={control}
                            render={({ field }) => (
                                <QuillEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    modules={excerptQuillModules}
                                    formats={excerptQuillFormats}
                                    placeholder="Enter a brief excerpt of the blog post..."
                                    height="120px"
                                    isExcerpt={true}
                                />
                            )}
                        />
                        <Text type="secondary" className="text-xs mt-1">
                            A short description that will appear in blog previews and
                            search results
                        </Text>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                            Content *
                        </label>
                        <Controller
                            name="content"
                            control={control}
                            rules={{ required: 'Content is required' }}
                            render={({ field }) => (
                                <QuillEditor
                                    value={field.value}
                                    onChange={field.onChange}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder="Write your blog content here..."
                                    height="300px"
                                    isExcerpt={false}
                                />
                            )}
                        />
                        {errors.content && (
                            <Text type="danger" className="text-xs mt-1">
                                {errors.content.message}
                            </Text>
                        )}
                    </div>
                </Card>

                {/* SEO */}
                <Card
                    title={
                        <div className="flex items-center">
                            <SearchOutlined className="mr-2 text-orange-600" />
                            <span>SEO Settings</span>
                        </div>
                    }
                    size="small"
                    className="shadow-sm border-l-4 border-l-orange-500"
                >
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    SEO URL
                                </label>
                                <Controller
                                    name="seo_url"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            placeholder="Enter SEO-friendly URL"
                                            prefix={
                                                <GlobalOutlined className="text-gray-400" />
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </Col>
                        <Col span={24}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Meta Title
                                </label>
                                <Controller
                                    name="meta_title"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            placeholder="Enter meta title for SEO"
                                        />
                                    )}
                                />
                            </div>
                        </Col>
                        <Col span={24}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Meta Description
                                </label>
                                <Controller
                                    name="meta_description"
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            {...field}
                                            placeholder="Enter meta description for SEO"
                                            rows={3}
                                        />
                                    )}
                                />
                            </div>
                        </Col>
                        <Col span={24}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Meta Keywords
                                </label>
                                <Controller
                                    name="meta_keywords"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            placeholder="Enter meta keywords (comma separated)"
                                        />
                                    )}
                                />
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 bg-gray-50 p-4 rounded-lg border">
                    <Button onClick={onCancel} size="large" className="px-6">
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        loading={isSubmitting}
                        icon={<SaveOutlined />}
                        className="px-6"
                    >
                        {isSubmitting
                            ? 'Saving...'
                            : initialData
                            ? 'Update Blog'
                            : 'Create Blog'}
                    </Button>
                </div>
            </form>
        </div>
    );
}
